import * as React from "react";
import { type VariantProps, cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border",
  {
    variants: {
      variant: {
        success:
          "bg-ev-success-bg text-ev-success-text border-ev-success-border",
        warning:
          "bg-ev-warning-bg text-ev-warning-text border-ev-warning-border",
        danger: "bg-ev-danger-bg text-ev-danger-text border-ev-danger-border",
        info: "bg-ev-info-bg text-ev-info-text border-ev-info-border",
        neutral:
          "bg-ev-surface-subtle text-ev-text-secondary border-ev-border",
        default: "bg-ev-disabled text-white border-ev-disabled",
        destructive: "bg-ev-danger text-white border-ev-danger",
        outline:
          "bg-transparent text-ev-text-primary border-ev-border",
        money: "bg-ev-green text-white border-ev-green",
        gradient:
          "bg-gradient-to-r from-ev-hero-blue to-ev-green text-white font-semibold border-transparent",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
);

const dotVariants: Record<string, string> = {
  success: "bg-ev-success-text",
  warning: "bg-ev-warning-text",
  danger: "bg-ev-danger-text",
  info: "bg-ev-info-text",
  neutral: "bg-ev-text-muted",
  default: "bg-white",
  destructive: "bg-white",
  outline: "bg-ev-text-primary",
  money: "bg-white",
  gradient: "bg-white",
};

interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
  icon?: React.ReactNode;
  count?: number;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = "neutral", dot = false, icon, count, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant }), className)}
        {...props}
      >
        {dot && (
          <span
            className={cn(
              "w-1.5 h-1.5 rounded-full",
              dotVariants[variant ?? "neutral"],
            )}
            aria-hidden="true"
          />
        )}
        {icon && (
          <span className="shrink-0" aria-hidden="true">
            {icon}
          </span>
        )}
        {count !== undefined ? count : children}
      </span>
    );
  },
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
